/* Modulo FAQ Categories — two-level accordion (category → question).
   Single/multi open at both levels (from data attributes), self-healing height
   model: open elements settle to max-height:none so nested content + font/resize
   reflow never clips. Vanilla IIFE, lazy-init via window.moduloLazyInit. */
(function () {
  'use strict';
  if (typeof window === 'undefined') return;

  var REDUCE = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  // Open: settle to 'none' (auto height, reflow-proof). With motion, animate to the
  // measured height first; transitionend then flips it to 'none'.
  function openHeight(el) {
    el.style.maxHeight = REDUCE ? 'none' : el.scrollHeight + 'px';
  }
  // Close: if currently free-flowing, pin to the measured height + reflow so the
  // transition has a start value, then collapse.
  function closeHeight(el) {
    if (el.style.maxHeight === 'none' || el.style.maxHeight === '') {
      el.style.maxHeight = el.scrollHeight + 'px';
      void el.offsetHeight; /* force reflow */
    }
    el.style.maxHeight = '0px';
  }

  function initSection(el) {
    if (el.dataset.fcaBound === '1') return;
    el.dataset.fcaBound = '1';

    var groupMode = el.getAttribute('data-group-mode') === 'multi' ? 'multi' : 'single';
    var qMode     = el.getAttribute('data-question-mode') === 'multi' ? 'multi' : 'single';
    var groups    = Array.prototype.slice.call(el.querySelectorAll('[data-fca-group]'));

    // While a group body is still pinned (mid open-transition), keep it tracking
    // nested question changes. Once it's settled to 'none' this is a no-op.
    function syncGroupHeight(group) {
      if (!group.classList.contains('is-open')) return;
      var body = group.querySelector('[data-fca-body]');
      if (body && body.style.maxHeight !== 'none') body.style.maxHeight = body.scrollHeight + 'px';
    }

    function openQuestion(item, group) {
      var trg = item.querySelector('[data-fca-q-trigger]');
      var ans = item.querySelector('[data-fca-answer]');
      item.classList.add('is-open');
      if (trg) trg.setAttribute('aria-expanded', 'true');
      if (ans) openHeight(ans);
      syncGroupHeight(group);
    }
    function closeQuestion(item, group) {
      var trg = item.querySelector('[data-fca-q-trigger]');
      var ans = item.querySelector('[data-fca-answer]');
      item.classList.remove('is-open');
      if (trg) trg.setAttribute('aria-expanded', 'false');
      if (ans) closeHeight(ans);
      syncGroupHeight(group);
    }
    function openGroup(group) {
      var trg = group.querySelector('[data-fca-group-trigger]');
      var body = group.querySelector('[data-fca-body]');
      group.classList.add('is-open');
      if (trg) trg.setAttribute('aria-expanded', 'true');
      if (body) openHeight(body);
      // Reveal the first question if none is open yet. Closed groups ship with all
      // questions collapsed (so SRs never hear a stale "expanded" inside a closed
      // category); opening the group restores the "first answer visible" feel.
      var qItems = group.querySelectorAll('[data-fca-q]');
      var anyOpen = false;
      for (var i = 0; i < qItems.length; i++) {
        if (qItems[i].classList.contains('is-open')) { anyOpen = true; break; }
      }
      if (!anyOpen && qItems.length) openQuestion(qItems[0], group);
    }
    function closeGroup(group) {
      var trg = group.querySelector('[data-fca-group-trigger]');
      var body = group.querySelector('[data-fca-body]');
      group.classList.remove('is-open');
      if (trg) trg.setAttribute('aria-expanded', 'false');
      if (body) closeHeight(body);
    }

    groups.forEach(function (group) {
      var body     = group.querySelector('[data-fca-body]');
      var gTrigger = group.querySelector('[data-fca-group-trigger]');
      var items    = Array.prototype.slice.call(group.querySelectorAll('[data-fca-q]'));

      items.forEach(function (item) {
        var trg = item.querySelector('[data-fca-q-trigger]');
        var ans = item.querySelector('[data-fca-answer]');
        if (!trg || !ans) return;

        ans.style.maxHeight = item.classList.contains('is-open') ? 'none' : '0px';

        ans.addEventListener('transitionend', function (e) {
          if (e.target !== ans || e.propertyName !== 'max-height') return;
          if (item.classList.contains('is-open')) {
            ans.style.maxHeight = 'none';
            syncGroupHeight(group);
          }
        });

        trg.addEventListener('click', function () {
          if (item.classList.contains('is-open')) {
            closeQuestion(item, group);
            return;
          }
          if (qMode === 'single') {
            items.forEach(function (other) {
              if (other !== item && other.classList.contains('is-open')) closeQuestion(other, group);
            });
          }
          openQuestion(item, group);
        });
      });

      if (body) {
        body.addEventListener('transitionend', function (e) {
          if (e.target !== body || e.propertyName !== 'max-height') return;
          if (group.classList.contains('is-open')) body.style.maxHeight = 'none';
        });
        body.style.maxHeight = group.classList.contains('is-open') ? 'none' : '0px';
      }

      // Bare (ungrouped) groups have no trigger — they stay open.
      if (gTrigger) {
        gTrigger.addEventListener('click', function () {
          if (group.classList.contains('is-open')) {
            closeGroup(group);
            return;
          }
          if (groupMode === 'single') {
            groups.forEach(function (other) {
              if (other !== group &&
                  other.classList.contains('is-open') &&
                  other.querySelector('[data-fca-group-trigger]')) {
                closeGroup(other);
              }
            });
          }
          openGroup(group);
        });
      }
    });

    // Resize safeguard: re-free open regions so re-wrapped content never clips.
    var resizeTimer = null;
    window.addEventListener('resize', function () {
      if (resizeTimer) clearTimeout(resizeTimer);
      resizeTimer = setTimeout(function () {
        groups.forEach(function (group) {
          if (!group.classList.contains('is-open')) return;
          var body = group.querySelector('[data-fca-body]');
          if (body && body.style.maxHeight !== '0px') body.style.maxHeight = 'none';
          group.querySelectorAll('[data-fca-q].is-open [data-fca-answer]').forEach(function (ans) {
            ans.style.maxHeight = 'none';
          });
        });
      }, 120);
    }, { passive: true });

    // Web-font load shifts text metrics — re-free open regions to auto height so
    // they re-measure without clipping (CLS guard). 'none' is metric-independent.
    if (document.fonts && document.fonts.ready) {
      document.fonts.ready.then(function () {
        groups.forEach(function (group) {
          if (!group.classList.contains('is-open')) return;
          var body = group.querySelector('[data-fca-body]');
          if (body && body.style.maxHeight !== '0px') body.style.maxHeight = 'none';
          group.querySelectorAll('[data-fca-q].is-open [data-fca-answer]').forEach(function (ans) {
            ans.style.maxHeight = 'none';
          });
        });
      });
    }
  }

  function bootstrap() {
    if (window.moduloLazyInit) {
      window.moduloLazyInit('[data-modulo-fca]', initSection);
    } else {
      var els = document.querySelectorAll('[data-modulo-fca]');
      for (var i = 0; i < els.length; i++) initSection(els[i]);
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', bootstrap);
  } else {
    bootstrap();
  }
})();
